import type { Solution } from '../entities/Sight';

/**
 * HUD en DOM superposé au canvas : rien n'est dessiné dans la scene 3D.
 * C'est plus simple a styler, accessible, et ca ne coute pas un draw call.
 *
 * Le reticule lui-meme est un SVG, pas une texture : il reste net a toutes
 * les resolutions et son echelle de hausse se rend visible par une classe CSS.
 */

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`element #${id} absent de index.html`);
  return node as T;
}

export function formatTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Degres a une decimale, avec la virgule francaise. */
function degrees(radians: number): string {
  return `${((radians * 180) / Math.PI).toFixed(1).replace('.', ',')}°`;
}

export class Hud {
  private readonly count = el('count');
  private readonly timer = el('timer');
  private readonly shots = el('shots');
  private readonly accuracy = el('accuracy');

  private readonly solutionPanel = el('solution');
  // Pas `id="range"` : tout element porte par un id devient une propriete
  // de `window`, et `window.range` est deja le bac a sable des hooks de
  // developpement. La collision faisait passer le bundle de production
  // pour un serveur de dev dans les tests.
  private readonly range = el('impact-range');
  private readonly flight = el('flight');
  private readonly elevation = el('elevation');

  private readonly reload = el('reload');
  private readonly reloadBar = el('reload-bar');
  private readonly reloadLabel = el('reload-label');

  /**
   * Le reticule est un <svg>, pas un <div>.
   *
   * `hidden` est une propriete de HTMLElement, et un SVGElement n'en est pas
   * un : `reticle.hidden = false` y cree une propriete JavaScript inerte sans
   * toucher a l'attribut, et le selecteur CSS `[hidden]` continue de masquer
   * l'element. Il faut passer par l'attribut.
   */
  private readonly reticle = el<Element & HTMLElement>('reticle');

  private readonly loading = el('loading');
  private readonly loadingText = el('loading-text');
  private readonly loadingBar = el('loading-bar');
  private readonly start = el('start');
  private readonly won = el('won');
  private readonly finalTime = el('final-time');
  private readonly finalShots = el('final-shots');
  private readonly finalAccuracy = el('final-accuracy');

  constructor(onStart: () => void, onReplay: () => void) {
    el<HTMLButtonElement>('start-btn').addEventListener('click', onStart);
    el<HTMLButtonElement>('replay-btn').addEventListener('click', onReplay);
  }

  setProgress(ratio: number, label?: string): void {
    this.loadingBar.style.width = `${Math.round(ratio * 100)}%`;
    if (label) this.loadingText.textContent = label;
  }

  showStart(): void {
    this.loading.hidden = true;
    this.start.hidden = false;
    this.won.hidden = true;
    this.reticle.setAttribute('hidden', '');
  }

  showPlaying(): void {
    this.loading.hidden = true;
    this.start.hidden = true;
    this.won.hidden = true;
    this.reticle.removeAttribute('hidden');
  }

  showWon(elapsed: number, fired: number, hits: number): void {
    this.finalTime.textContent = formatTime(elapsed);
    this.finalShots.textContent = String(fired);
    this.finalAccuracy.textContent = ratio(hits, fired);
    this.won.hidden = false;
    this.reticle.setAttribute('hidden', '');
  }

  showError(message: string): void {
    this.loading.hidden = false;
    this.loadingText.textContent = message;
    this.loadingBar.style.width = '100%';
  }

  setTargets(destroyed: number, total: number): void {
    this.count.textContent = `${destroyed} / ${total}`;
    this.count.classList.remove('pulse');
    // Forcer un reflow relance l'animation CSS meme sur deux coups au but
    // rapproches.
    void this.count.offsetWidth;
    this.count.classList.add('pulse');
  }

  setTime(elapsed: number): void {
    this.timer.textContent = formatTime(elapsed);
  }

  setShots(fired: number, hits: number): void {
    this.shots.textContent = String(fired);
    this.accuracy.textContent = fired === 0 ? '—' : ratio(hits, fired);
  }

  /**
   * Affiche la solution de tir : ou l'obus retombera, en combien de temps, et
   * sous quel angle le tube est pointe.
   *
   * La hausse affichee est le site *reel* mesure dans le monde, pas l'angle
   * mecanique du canon : sur une pente, les deux diffèrent de plusieurs
   * degres, et c'est le site reel qui decide de la portee.
   */
  setSolution(solution: Solution | null, worldPitch: number): void {
    this.elevation.textContent = degrees(worldPitch);
    if (!solution) {
      this.range.textContent = '—';
      this.flight.textContent = '—';
      this.solutionPanel.classList.remove('locked');
      this.reticle.classList.remove('locked');
      return;
    }
    this.range.textContent = solution.overshoot
      ? '∞'
      : `${Math.round(solution.range)} m`;
    this.flight.textContent = solution.overshoot
      ? '—'
      : `${solution.flightTime.toFixed(1).replace('.', ',')} s`;
    this.solutionPanel.classList.toggle('locked', solution.onTarget);
    this.reticle.classList.toggle('locked', solution.onTarget);
  }

  /** @param ratio 0 juste apres le coup, 1 quand le canon est pret. */
  setReload(filled: number): void {
    const ready = filled >= 1;
    this.reloadBar.style.width = `${Math.round(filled * 100)}%`;
    this.reload.classList.toggle('ready', ready);
    this.reloadLabel.textContent = ready ? 'Pièce chargée' : 'Chargement';
  }

  setSightMode(active: boolean): void {
    this.reticle.classList.toggle('sight', active);
  }
}

function ratio(hits: number, fired: number): string {
  if (fired === 0) return '0 %';
  return `${Math.round((hits / fired) * 100)} %`;
}
